// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Measured outside-air temperature for a plan's day, aligned to its time axis.
 *
 * The ambient card's forecast curve is the optimizer's own — what the plan was
 * solved against, hours before it ran. This is the other half: what the weather
 * actually did, from the Weather API's observations route, so the two can be
 * read against each other on one axis.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getObservations, toTemperatureSeries } from '../services/weatherService';
import { WEATHER_POLL_INTERVAL_MS } from '../config/queryConfig';
import { fillInteriorGaps, resampleToAxis } from '../utils/telemetryResample';
import { axisStartMs } from '../utils/planSeries';
import type { TimeAxis } from '../types';

const HOUR_MS = 60 * 60_000;

/** `now` rounded up to the next whole hour, as an ISO-8601 instant. */
function hourAlignedEnd(now: Date): string {
  return new Date(Math.ceil(now.getTime() / HOUR_MS) * HOUR_MS).toISOString();
}

/** Celsius readings converted so they share the card's °F axis. */
function toFahrenheitValues(
  values: (number | null)[],
  unit: string | undefined
): (number | null)[] {
  const celsius = (unit ?? '').toLowerCase().includes('c');
  if (!celsius) return values;
  return values.map(v => (v === null || !Number.isFinite(v) ? v : v * 1.8 + 32));
}

export interface UseAmbientObservationsResult {
  /** Axis-aligned °F readings; `null` where nothing was measured. */
  values: (number | null)[];
  loading: boolean;
  /** The request failed and nothing is cached: an outage, not an empty window. */
  failed: boolean;
}

/**
 * The observations upstream is hourly, so this polls on its own hourly clock
 * ({@link WEATHER_POLL_INTERVAL_MS}) rather than the fast measured-curve cadence
 * the telemetry hooks use — there is no sub-hour reading to chase, and asking
 * for one 404s the relay.
 *
 * The window opens at the axis start and closes at the next whole hour. Both
 * ends are hour-aligned because the relay rejects a window that is not, and the
 * whole day is only ~24 hourly points, so re-reading it each poll is cheap and
 * sidesteps the delta/merge accumulation the 15-minute telemetry curves need.
 * The end is rounded rather than left at `now` so it is stable within the hour,
 * keeping the query on one cache entry between polls.
 */
export function useAmbientObservations(
  timeAxis: TimeAxis | undefined,
  siteId: string
): UseAmbientObservationsResult {
  const axisStart = timeAxis ? axisStartMs(timeAxis) : undefined;
  const start = axisStart !== undefined ? new Date(axisStart).toISOString() : undefined;
  const enabled = Boolean(siteId && start && timeAxis);
  const queryKey = ['weather', 'observations', siteId, start ?? ''];

  const { data, isPending, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const end = hourAlignedEnd(new Date());
      const envelope = await getObservations({ siteId, start: start as string, end });
      return toTemperatureSeries(envelope);
    },
    enabled,
    refetchInterval: WEATHER_POLL_INTERVAL_MS,
    // Keep the previous curve across a plan rollover (same site, new axis) so
    // the card does not fall back to "forecast only" while that window is in
    // flight. A different site must not reuse it: the axes do not match, and
    // the chart would draw the other site's temperatures.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === siteId ? previousData : undefined,
  });

  return useMemo(() => {
    if (!timeAxis || !data) {
      return { values: [], loading: enabled && isPending, failed: isError && !data };
    }
    // Fill the gaps between hourly readings so the sub-15-minute cadence draws
    // as a line; leading/trailing nulls stay put so the curve still stops where
    // measurement stops.
    const filled = fillInteriorGaps(resampleToAxis(data.points, timeAxis));
    return {
      values: toFahrenheitValues(filled, data.unit),
      loading: false,
      failed: false,
    };
  }, [data, timeAxis, enabled, isPending, isError]);
}
